import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';
import type { PermissionCode } from '@/constants/permissions';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/80',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  grant,
  invalidGrantAction = 'hidden',
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /**
     * 权限码，传入后会判断用户是否有该权限，没有则不渲染
     */
    grant?: PermissionCode;
    /**
     * 没权限时隐藏或者禁用，禁用时会有Tooltip提示没有权限
     * @default 'hidden' 隐藏
     */
    invalidGrantAction?: 'hidden' | 'disabled';
  }) {
  const Comp = asChild ? Slot : 'button';
  const permissionCodes = useUserStore((state) => state.userInfo?.permissionCodes);

  if (
    grant &&
    invalidGrantAction === 'hidden' &&
    (!permissionCodes || !permissionCodes.includes(grant))
  ) {
    return null;
  }
  if (
    grant &&
    invalidGrantAction === 'disabled' &&
    (!permissionCodes || !permissionCodes.includes(grant))
  ) {
    return (
      <Tooltip>
        <TooltipTrigger>
          <Comp
            type="button"
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }), 'cursor-pointer')}
            disabled
            {...props}
          />
        </TooltipTrigger>
        <TooltipContent>您没有权限进行该操作，请联系管理员授权</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Comp
      type="button"
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }), 'cursor-pointer')}
      {...props}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
